import type { EntityType, KYCStatus } from '../../types';

// ── Generic Badge ─────────────────────────────────────────────────────────────

type BadgeVariant =
  | 'active' | 'inactive'
  | 'owner' | 'staff' | 'free' | 'primary'
  | 'customer' | 'supplier' | 'broker' | 'shipping' | 'insurance' | 'lab'
  | 'kyc-verified' | 'kyc-pending' | 'kyc-not-submitted' | 'kyc-na';

interface BadgeProps {
  variant: BadgeVariant;
  children: React.ReactNode;
}

export function Badge({ variant, children }: BadgeProps) {
  return <span className={`badge badge-${variant}`}>{children}</span>;
}

// ── EntityTypeBadge ───────────────────────────────────────────────────────────

const ENTITY_LABEL: Record<EntityType, string> = {
  customer:  'Customer',
  supplier:  'Supplier',
  broker:    'Broker',
  shipping:  'Shipping',
  insurance: 'Insurance',
  lab:       'Lab',
};

export function EntityTypeBadge({ type }: { type: EntityType }) {
  return <Badge variant={type}>{ENTITY_LABEL[type]}</Badge>;
}

// ── KYCBadge ─────────────────────────────────────────────────────────────────

const KYC_MAP: Record<KYCStatus, { variant: BadgeVariant; label: string }> = {
  'verified':      { variant: 'kyc-verified',      label: 'Verified'       },
  'pending':       { variant: 'kyc-pending',        label: 'Pending'        },
  'not-submitted': { variant: 'kyc-not-submitted',  label: 'Not Submitted'  },
};

export function KYCBadge({ status, types }: { status: KYCStatus; types: EntityType[] }) {
  const needsKYC = (types ?? []).includes('customer') || (types ?? []).includes('supplier');
  if (!needsKYC) return <Badge variant="kyc-na">—</Badge>;
  // `status` is typed as always one of the 3 known values, but a legacy or
  // manually-edited Firestore doc can carry something else (missing field,
  // stale value from before KYCStatus's options changed) — KYC_MAP[status]
  // would then be undefined and crash the whole page on destructure. Fall
  // back to the "not submitted" look rather than throwing.
  const { variant, label } = KYC_MAP[status] ?? KYC_MAP['not-submitted'];
  return <Badge variant={variant}>{label}</Badge>;
}
